import { useCallback } from 'react';
import dayjs from 'dayjs';

import { useSession } from '~/contexts/SessionContext/SessionContext';
import {
  createActivity,
  fetchActivityListOptions,
} from '~/services/activity/activityApi';
import type { ILevels } from '~/services/programs/programsApi.types';
import { Crashlytics } from '~/utils/crashlytics';

const ACTIVITY_TYPES_GROUP = 'Outros';
const CLASS_ACTIVITY_NAME = 'Aula';
const PENDING_STATUS_NAME = 'pending';

const getIntensityNameByLevelId = (levelId: number) => {
  if (levelId === 3) {
    return 'Intenso';
  }

  if (levelId === 2) {
    return 'Moderado';
  }

  return 'Leve';
};

interface ICreateProgramActivityParams {
  contentId: number;
  level: ILevels;
}

export const useCreateProgramActivity = () => {
  const { currentUser } = useSession();
  const currentUserId = currentUser?.id;

  const createProgramActivity = useCallback(
    async ({ contentId, level }: ICreateProgramActivityParams) => {
      if (!currentUserId || !contentId || !level?.id) {
        return;
      }

      try {
        const activityListOptions = await fetchActivityListOptions();
        const classId = activityListOptions.types[ACTIVITY_TYPES_GROUP]?.find(
          (type) => type.name === CLASS_ACTIVITY_NAME
        )?.id;
        const pendingStatusId = activityListOptions.status.find(
          (status) => status.name === PENDING_STATUS_NAME
        )?.id;
        const intensityId = activityListOptions.intensities.find(
          (intensity) => intensity.name === getIntensityNameByLevelId(level.id)
        )?.id;

        if (!classId || !pendingStatusId || !intensityId) {
          Crashlytics.handleException(
            new Error('Missing activity configuration for TV class event'),
            'Create program activity'
          );
          return;
        }

        await createActivity({
          userId: currentUserId,
          typeId: classId,
          intensityId,
          statusId: pendingStatusId,
          date: dayjs().format('YYYY-MM-DD'),
          time: 0,
          contentId,
        });
      } catch (error) {
        Crashlytics.handleException(error, 'Create program activity');
      }
    },
    [currentUserId]
  );

  return { createProgramActivity };
};
